/**
 * DriverRoutesPage - Lista de rotas do motorista
 *
 * Funcionalidades:
 * - Lista de rotas atribuidas
 * - Filtro por status
 * - Acoes rapidas (aceitar, iniciar, ver detalhes)
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  LocalShipping as VehicleIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Straighten as DistanceIcon,
  Place as StopIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import driverAssignmentService, { ASSIGNMENT_STATUS } from '../routing/services/driverAssignmentService';

// Componente de card de rota
const RouteCard = ({ route, onPress, onAction, tokens }) => {
  const getStatusConfig = () => {
    if (route.status === 'executing') {
      return {
        label: 'Em Execucao',
        color: tokens?.semantic?.success,
        icon: VehicleIcon,
      };
    }
    if (route.status === 'completed') {
      return {
        label: 'Concluida',
        color: tokens?.accent?.primary,
        icon: CompleteIcon,
      };
    }
    if (route.assignmentStatus === ASSIGNMENT_STATUS.PENDING) {
      return {
        label: 'Aguardando Aceite',
        color: tokens?.semantic?.warning,
        icon: PendingIcon,
      };
    }
    if (route.assignmentStatus === ASSIGNMENT_STATUS.ACCEPTED) {
      return {
        label: 'Aceita',
        color: tokens?.semantic?.success,
        icon: CheckIcon,
      };
    }
    return {
      label: route.status || 'Pendente',
      color: tokens?.text?.tertiary,
      icon: PendingIcon,
    };
  };

  const status = getStatusConfig();
  const StatusIcon = status.icon;

  return (
    <Card
      sx={{
        backgroundColor: tokens?.surface?.raised,
        borderRadius: tokens?.radius?.lg,
        border: `1px solid ${tokens?.border?.subtle}`,
        mb: 2,
      }}
    >
      <CardActionArea onClick={() => onPress(route)}>
        <CardContent sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontWeight: 600,
                  fontSize: '16px',
                  mb: 0.5,
                }}
              >
                {route.name}
              </Typography>
              {route.description && (
                <Typography
                  sx={{
                    color: tokens?.text?.tertiary,
                    fontSize: '13px',
                  }}
                >
                  {route.description}
                </Typography>
              )}
            </Box>
            <Chip
              icon={<StatusIcon sx={{ fontSize: '14px !important' }} />}
              label={status.label}
              size="small"
              sx={{
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontWeight: 600,
                fontSize: '10px',
                '& .MuiChip-icon': {
                  color: status.color,
                },
              }}
            />
          </Box>

          {/* Metricas */}
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StopIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                {route.stopsCount || 0} paradas
              </Typography>
            </Box>
            {route.totalDistance > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DistanceIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                  {(route.totalDistance / 1000).toFixed(1)} km
                </Typography>
              </Box>
            )}
            {route.totalDuration > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                  {Math.round(route.totalDuration / 60)} min
                </Typography>
              </Box>
            )}
          </Box>

          {/* Acoes */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {route.assignmentStatus === ASSIGNMENT_STATUS.PENDING && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={(e) => { e.stopPropagation(); onAction('accept', route); }}
                  sx={{
                    backgroundColor: tokens?.semantic?.success,
                    '&:hover': { backgroundColor: '#16a34a' },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Aceitar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={(e) => { e.stopPropagation(); onAction('reject', route); }}
                  sx={{
                    borderColor: tokens?.semantic?.error,
                    color: tokens?.semantic?.error,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Recusar
                </Button>
              </>
            )}
            {route.assignmentStatus === ASSIGNMENT_STATUS.ACCEPTED && route.status !== 'executing' && (
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<StartIcon />}
                onClick={(e) => { e.stopPropagation(); onAction('start', route); }}
                sx={{
                  backgroundColor: tokens?.accent?.primary,
                  '&:hover': { backgroundColor: tokens?.accent?.hover },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Iniciar Rota
              </Button>
            )}
            {route.status === 'executing' && (
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<ArrowIcon />}
                onClick={(e) => { e.stopPropagation(); onAction('continue', route); }}
                sx={{
                  backgroundColor: tokens?.accent?.primary,
                  '&:hover': { backgroundColor: tokens?.accent?.hover },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Continuar
              </Button>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Icon de check para status aceito
const CheckIcon = CompleteIcon;

const DriverRoutesPage = () => {
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab atual baseada no query param
  const currentTab = searchParams.get('status') || 'all';

  // Carregar rotas
  const loadRoutes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const myRoutes = await driverAssignmentService.getMyRoutes();
      setRoutes(myRoutes);
    } catch (err) {
      console.error('[DriverRoutesPage] Error:', err);
      setError('Erro ao carregar rotas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffectAsync(async () => {
    await loadRoutes();
  }, []);

  // Filtrar rotas por tab
  const filteredRoutes = useMemo(() => {
    switch (currentTab) {
      case 'pending':
        return routes.filter((r) => r.assignmentStatus === ASSIGNMENT_STATUS.PENDING);
      case 'accepted':
        return routes.filter((r) => r.assignmentStatus === ASSIGNMENT_STATUS.ACCEPTED && r.status !== 'executing');
      case 'executing':
        return routes.filter((r) => r.status === 'executing');
      case 'completed':
        return routes.filter((r) => r.status === 'completed');
      default:
        return routes;
    }
  }, [routes, currentTab]);

  // Contadores para badges
  const counts = useMemo(() => ({
    all: routes.length,
    pending: routes.filter((r) => r.assignmentStatus === ASSIGNMENT_STATUS.PENDING).length,
    accepted: routes.filter((r) => r.assignmentStatus === ASSIGNMENT_STATUS.ACCEPTED && r.status !== 'executing').length,
    executing: routes.filter((r) => r.status === 'executing').length,
    completed: routes.filter((r) => r.status === 'completed').length,
  }), [routes]);

  // Handlers
  const handleTabChange = useCallback((event, newValue) => {
    setSearchParams({ status: newValue });
  }, [setSearchParams]);

  const handleRoutePress = useCallback((route) => {
    navigate(`/driver/routes/${route.id}`);
  }, [navigate]);

  const handleRouteAction = useCallback(async (action, route) => {
    try {
      switch (action) {
        case 'accept':
          await driverAssignmentService.acceptAssignment(route.id, route.assignedVehicle?.id);
          await loadRoutes();
          break;
        case 'reject':
          navigate(`/driver/routes/${route.id}/reject`);
          break;
        case 'start':
          navigate(`/driver/routes/${route.id}/execute`);
          break;
        case 'continue':
          navigate(`/driver/routes/${route.id}/execute`);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('[DriverRoutesPage] Action error:', err);
    }
  }, [navigate, loadRoutes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header com tabs */}
      <Box
        sx={{
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Minhas Rotas
          </Typography>
          <IconButton size="small" onClick={loadRoutes} sx={{ color: tokens?.text?.tertiary }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              color: tokens?.text?.tertiary,
              '&.Mui-selected': {
                color: tokens?.accent?.primary,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: tokens?.accent?.primary,
            },
          }}
        >
          <Tab value="all" label={`Todas (${counts.all})`} />
          <Tab value="pending" label={`Pendentes (${counts.pending})`} />
          <Tab value="accepted" label={`Aceitas (${counts.accepted})`} />
          <Tab value="executing" label={`Em Rota (${counts.executing})`} />
          <Tab value="completed" label={`Concluidas (${counts.completed})`} />
        </Tabs>
      </Box>

      {/* Conteudo */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <>
            <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={150} />
          </>
        )}

        {/* Lista de rotas */}
        {!isLoading && filteredRoutes.length > 0 && (
          filteredRoutes.map((route) => (
            <RouteCard
              key={route.id}
              route={route}
              onPress={handleRoutePress}
              onAction={handleRouteAction}
              tokens={tokens}
            />
          ))
        )}

        {/* Sem rotas */}
        {!isLoading && filteredRoutes.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
            }}
          >
            <VehicleIcon sx={{ fontSize: 64, color: tokens?.text?.tertiary, mb: 2 }} />
            <Typography
              sx={{
                color: tokens?.text?.secondary,
                fontSize: '16px',
                mb: 1,
              }}
            >
              Nenhuma rota encontrada
            </Typography>
            <Typography
              sx={{
                color: tokens?.text?.tertiary,
                fontSize: '14px',
              }}
            >
              {currentTab === 'all'
                ? 'Aguarde a atribuicao de novas rotas'
                : 'Nao ha rotas nesta categoria'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DriverRoutesPage;
